// src/components/pages/auth/SignUpPage.js
import React, { useState } from "react";
import {
  Button,
  Container,
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { TOP_APP_BAR_HEIGHT } from "../../../constants/constants";
import { useNavigate } from "react-router-dom";
import {
  signUpUser,
  getFriendlyErrorMessage,
} from "../../../services/authService";
import {
  fetchUserProfileFromFirestore,
  getOrCreateUserProfile,
} from "../../../services/profileService";
import useStore from "../../../store/useStore";
import useAuthStore from "../../../store/useAuthStore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const SignUpPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Global auth/profile store hooks.
  const { setUser } = useAuthStore();
  const { setUserProfile } = useStore();

  const handleSignUp = async () => {
    setLoading(true);
    setError(null);
    try {
      // Create a new user.
      const user = await signUpUser(email, password);
      console.log("User signed up successfully:", user);
      setUser(user);

      // Fetch or create the Firestore profile.
      let profile = await fetchUserProfileFromFirestore(user.uid);
      if (!profile) {
        profile = await getOrCreateUserProfile(user);
      }
      setUserProfile(profile);

      // Navigate based on phone number verification.
      if (profile.phoneNumber) {
        navigate("/profile/settings", { replace: true });
      } else {
        navigate("/phone-otp", { replace: true });
      }
    } catch (error) {
      console.error("Error signing up:", error.message);
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing up...
          </Typography>
        </Box>
      </Backdrop>

      <Container
        maxWidth="xs"
        sx={{
          backgroundColor: "white",
          height: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
        }}
      >
        <Box sx={{ pt: 4, px: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              textAlign: "left",
              mb: 2,
            }}
          >
            Sign Up with Email Address
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                variant="outlined"
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    "& fieldset": {
                      borderColor: theme.colors.blue, // Default border color
                    },
                    "&:hover fieldset": {
                      borderColor: theme.colors.blue, // Border color on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.colors.blue, // Border color when focused
                    },
                  },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="Create Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    "& fieldset": {
                      borderColor: theme.colors.blue, // Default border color
                    },
                    "&:hover fieldset": {
                      borderColor: theme.colors.blue, // Border color on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.colors.blue, // Border color when focused
                    },
                  },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleSignUp}
                variant="contained"
                sx={(theme) => ({
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "14px 40px",
                  marginBottom: "10px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                })}
              >
                Sign Up
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography align="left">
                Already a member?{" "}
                <Link
                  onClick={() => navigate("/signin")}
                  underline="hover"
                  sx={(theme) => ({
                    cursor: "pointer",
                    color: theme.colors.blue,
                  })}
                >
                  Sign in!
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUpPage;
